/**
 * useOfflineSync
 *
 * Tracks real-time network status and auto-syncs the offline timesheet queue
 * when connectivity is restored.
 *
 * Uses:
 * - navigator.onLine + window online/offline events (web + Capacitor WebView)
 * - @capacitor/network if available (more accurate on native)
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { count } from '../services/offlineQueue';
import { syncPendingTimesheets, type SyncReport } from '../services/timesheetOffline';

export interface OfflineSyncState {
  isOnline: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastSyncReport: SyncReport | null;
  /** Manually trigger a sync attempt */
  syncNow: () => Promise<void>;
  /** Refresh the pending count badge */
  refreshCount: () => Promise<void>;
}

export function useOfflineSync(): OfflineSyncState {
  // Antag online til det modsatte er BEKRÆFTET: Chrome kan rapportere
  // navigator.onLine=false permanent (VPN/virtuelle adaptere) selv om nettet
  // virker. Kun en fejlet probe mod egen origin tæller som offline.
  const [isOnline, setIsOnline] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncReport, setLastSyncReport] = useState<SyncReport | null>(null);
  // Prevent concurrent syncs
  const syncingRef = useRef(false);

  const refreshCount = useCallback(async () => {
    try {
      const n = await count();
      setPendingCount(n);
    } catch {
      // IndexedDB not available in this context — no-op
    }
  }, []);

  const doSync = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setIsSyncing(true);
    try {
      const report = await syncPendingTimesheets();
      setLastSyncReport(report);
      await refreshCount();
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [refreshCount]);

  const syncNow = useCallback(async () => {
    if (!isOnline) return;
    await doSync();
  }, [isOnline, doSync]);

  useEffect(() => {
    // Initial count
    refreshCount();

    // navigator.onLine fanget ved mount kan være forældet (netværksskifte under
    // load affyrer 'online' før lytterne er på) — gensynkronisér ved effect-start
    // og når fanen får fokus, så banneret ikke hænger fast.
    const resync = async () => {
      if (navigator.onLine) {
        setIsOnline(true);
        return;
      }
      try {
        await fetch(`/manifest.webmanifest?probe=${Date.now()}`, { method: 'HEAD', cache: 'no-store' });
        setIsOnline(true);
      } catch {
        setIsOnline(false);
      }
    };
    resync();
    window.addEventListener('focus', resync);

    const handleOnline = async () => {
      setIsOnline(true);
      await doSync();
    };

    const handleOffline = () => {
      resync();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also sync on tab visibility restore (e.g. user returns to the app after being in background)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        await doSync();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', resync);
    window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [doSync, refreshCount]);

  return { isOnline, pendingCount, isSyncing, lastSyncReport, syncNow, refreshCount };
}
